import { getResponsiveDimensions } from "../theme/style";

export interface LayoutConfig {
  width: number;
  height: number;
  padding: number;
  bottleWidth: number;
  bottleHeight: number;
  bottleSpacing: number;
  slotWidth: number;
  slotHeight: number;
  slotSpacing: number;
  headerHeight: number;
  headerTitleSize: number;
  headerInfoSize: number;
  hudHeight: number;
  buttonHeight: number;
  buttonSpacing: number;
  // Responsive layout info
  scale: number;
  isMobile: boolean;
  isSmallMobile: boolean;
}

export interface LayoutMetrics {
  bottlesPerRow: number;
  bottleRows: number;
  bottleGridWidth: number;
  bottleGridHeight: number;
  bottleGridX: number;
  bottleGridY: number;
  slotsX: number;
  slotsY: number;
  slotsWidth: number;
  slotsHeight: number;
  hudY: number;
  buttonsX: number;
  buttonsY: number;
  buttonsWidth: number;
  buttonsHeight: number;
}

export class LayoutManager {
  private config: LayoutConfig;
  private metrics: LayoutMetrics | null = null;
  private metricsKey: string | null = null;

  constructor(config: Partial<LayoutConfig> = {}) {
    const responsive = getResponsiveDimensions(
      config.width ?? window.innerWidth,
      config.height ?? window.innerHeight
    );
    
    this.config = {
      width: config.width ?? window.innerWidth,
      height: config.height ?? window.innerHeight,
      padding: responsive.isSmallMobile ? 8 : responsive.isMobile ? 12 : 20,
      bottleWidth: responsive.bottle.width,
      bottleHeight: responsive.bottle.height,
      bottleSpacing: responsive.isSmallMobile ? 8 : responsive.isMobile ? 10 : 15,
      slotWidth: responsive.slot.width,
      slotHeight: responsive.slot.height,
      slotSpacing: responsive.isSmallMobile ? 6 : responsive.isMobile ? 8 : 15,
      headerHeight: responsive.header.height,
      headerTitleSize: responsive.header.titleSize,
      headerInfoSize: responsive.header.infoSize,
      hudHeight: responsive.hud.height,
      buttonHeight: responsive.button.height,
      buttonSpacing: responsive.isSmallMobile ? 6 : responsive.isMobile ? 8 : 10,
      scale: responsive.scale,
      isMobile: responsive.isMobile,
      isSmallMobile: responsive.isSmallMobile,
      ...config
    };
  }

  updateDimensions(width: number, height: number): void {
    const responsive = getResponsiveDimensions(width, height);
    
    this.config.width = width;
    this.config.height = height;
    this.config.padding = responsive.isSmallMobile ? 8 : responsive.isMobile ? 12 : 20;
    this.config.bottleWidth = responsive.bottle.width;
    this.config.bottleHeight = responsive.bottle.height;
    this.config.bottleSpacing = responsive.isSmallMobile ? 8 : responsive.isMobile ? 10 : 15;
    this.config.slotWidth = responsive.slot.width;
    this.config.slotHeight = responsive.slot.height;
    this.config.slotSpacing = responsive.isSmallMobile ? 6 : responsive.isMobile ? 8 : 15;
    this.config.headerHeight = responsive.header.height;
    this.config.headerTitleSize = responsive.header.titleSize;
    this.config.headerInfoSize = responsive.header.infoSize;
    this.config.hudHeight = responsive.hud.height;
    this.config.buttonHeight = responsive.button.height;
    this.config.buttonSpacing = responsive.isSmallMobile ? 6 : responsive.isMobile ? 8 : 10;
    this.config.scale = responsive.scale;
    this.config.isMobile = responsive.isMobile;
    this.config.isSmallMobile = responsive.isSmallMobile;
    
    this.metrics = null;
    this.metricsKey = null;
  }

  calculateMetrics(bottleCount: number, slotCount: number): LayoutMetrics {
    const key = `${bottleCount}:${slotCount}`;
    if (this.metrics && this.metricsKey === key) {
      return this.metrics;
    }

    const padding = this.config.padding;
    const buttonHeight = this.config.buttonHeight;
    const buttonAreaHeight = buttonHeight + padding * 2;
    
    // HUD at bottom
    const hudHeight = this.config.hudHeight;
    const hudY = this.config.height - padding - hudHeight;
    
    // Slots area at top
    const slotAreaHeight = this.config.headerHeight + this.config.slotHeight + padding;
    
    // Available space for bottles (between slots at top and buttons + HUD at bottom)
    const availableHeight = this.config.height - slotAreaHeight - buttonAreaHeight - hudHeight - padding * 2;
    const availableWidth = this.config.width - padding * 2;

    // Calculate bottle grid layout
    const perRowByWidth = Math.max(1, Math.floor((availableWidth + this.config.bottleSpacing) / (this.config.bottleWidth + this.config.bottleSpacing)));
    let bottlesPerRow = perRowByWidth;
    if (this.config.isMobile) {
      const maxPerRow = this.config.isSmallMobile ? 3 : 4;
      bottlesPerRow = Math.min(bottlesPerRow, maxPerRow);
    }

    let bottleRows = Math.ceil(bottleCount / bottlesPerRow);
    let bottleGridWidth = bottlesPerRow * this.config.bottleWidth + (bottlesPerRow - 1) * this.config.bottleSpacing;
    let bottleGridHeight = bottleRows * this.config.bottleHeight + (bottleRows - 1) * this.config.bottleSpacing;

    if (bottleGridHeight > availableHeight && bottleCount > 0) {
      const maxRows = Math.max(1, Math.floor((availableHeight + this.config.bottleSpacing) / (this.config.bottleHeight + this.config.bottleSpacing)));
      const minPerRow = Math.ceil(bottleCount / maxRows);
      bottlesPerRow = Math.min(perRowByWidth, Math.max(bottlesPerRow, minPerRow));
      bottleRows = Math.ceil(bottleCount / bottlesPerRow);
      bottleGridWidth = bottlesPerRow * this.config.bottleWidth + (bottlesPerRow - 1) * this.config.bottleSpacing;
      bottleGridHeight = bottleRows * this.config.bottleHeight + (bottleRows - 1) * this.config.bottleSpacing;
    }

    const bottleGridX = (this.config.width - bottleGridWidth) / 2;
    // Center bottles in available space, below slots and above buttons
    const bottleGridY = slotAreaHeight + (availableHeight - bottleGridHeight) / 2;

    // Slots positioned at top
    const slotsWidth = slotCount * this.config.slotWidth + (slotCount - 1) * this.config.slotSpacing;
    const slotsX = (this.config.width - slotsWidth) / 2;
    const slotsY = padding + this.config.headerHeight;

    // Buttons above HUD
    const buttonWidth = this.config.isMobile ? 70 : 100;
    const buttonCount = 2;
    const buttonsWidth = buttonCount * buttonWidth + (buttonCount - 1) * this.config.buttonSpacing;
    const buttonsX = (this.config.width - buttonsWidth) / 2;
    const buttonsY = hudY - padding - buttonHeight;

    this.metrics = {
      bottlesPerRow,
      bottleRows,
      bottleGridWidth,
      bottleGridHeight,
      bottleGridX,
      bottleGridY,
      slotsX,
      slotsY,
      slotsWidth,
      slotsHeight: this.config.slotHeight,
      hudY,
      buttonsX,
      buttonsY,
      buttonsWidth,
      buttonsHeight: buttonHeight
    };
    this.metricsKey = key;

    return this.metrics;
  }

  getBottlePosition(index: number, bottleCount: number, slotCount: number): { x: number; y: number } {
    const metrics = this.calculateMetrics(bottleCount, slotCount);
    const row = Math.floor(index / metrics.bottlesPerRow);
    const col = index % metrics.bottlesPerRow;

    const x = metrics.bottleGridX + col * (this.config.bottleWidth + this.config.bottleSpacing);
    const y = metrics.bottleGridY + row * (this.config.bottleHeight + this.config.bottleSpacing);

    return { x, y };
  }

  getSlotPosition(index: number, slotCount: number): { x: number; y: number } {
    const metrics = this.calculateMetrics(0, slotCount);
    const x = metrics.slotsX + index * (this.config.slotWidth + this.config.slotSpacing);
    const y = metrics.slotsY;

    return { x, y };
  }

  getHUDPosition(): { x: number; y: number; width: number; height: number } {
    const metrics = this.calculateMetrics(0, 0);
    return {
      x: this.config.padding,
      y: metrics.hudY,
      width: this.config.width - this.config.padding * 2,
      height: this.config.hudHeight
    };
  }

  getButtonPosition(buttonIndex: number): { x: number; y: number; width: number; height: number } {
    const metrics = this.calculateMetrics(0, 0);
    const buttonWidth = this.config.isMobile ? 70 : 100;
    const x = metrics.buttonsX + buttonIndex * (buttonWidth + this.config.buttonSpacing);

    return {
      x,
      y: metrics.buttonsY,
      width: buttonWidth,
      height: metrics.buttonsHeight
    };
  }

  getBottleVisualMetrics(): { width: number; height: number; offsetX: number; offsetY: number } {
    const widthInset = this.config.isMobile
      ? Math.max(6, Math.floor(this.config.bottleWidth * 0.12))
      : 10;
    const heightInset = this.config.isMobile
      ? Math.max(20, Math.floor(this.config.bottleHeight * 0.16))
      : 50;

    const width = Math.max(1, this.config.bottleWidth - widthInset);
    const height = Math.max(1, this.config.bottleHeight - heightInset);
    const offsetX = Math.max(0, Math.floor((this.config.bottleWidth - width) / 2));
    const offsetY = Math.max(0, Math.floor((this.config.bottleHeight - height) / 2));

    return { width, height, offsetX, offsetY };
  }

  getConfig(): LayoutConfig {
    return { ...this.config };
  }

  setConfig(config: Partial<LayoutConfig>): void {
    this.config = { ...this.config, ...config };
    this.metrics = null;
    this.metricsKey = null;
  }

  reset(): void {
    this.metrics = null;
    this.metricsKey = null;
  }
}

export function createLayoutManager(config?: Partial<LayoutConfig>): LayoutManager {
  return new LayoutManager(config);
}
